import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsSubmenuOpen, setProductsSubmenuOpen] = useState(false);
  const submenuRef = useRef(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setProductsSubmenuOpen(false);
  };

  const toggleProductsSubmenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setProductsSubmenuOpen(!productsSubmenuOpen);
  };

  const solutionGridItems = [
    {
      label: 'Water Monitoring',
      icon: '/images/menu_icons/water.png',
      description: 'Track water quality, usage patterns, and compliance in real time.',
      to: '/industry-solutions/water-monitoring'
    },
    {
      label: 'Energy Metering',
      icon: '/images/menu_icons/energy.png',
      description: 'Measure and optimize power consumption across connected assets.',
      to: '/industry-solutions/energy-metering'
    },
    {
      label: 'Air Quality',
      icon: '/images/menu_icons/air.png',
      description: 'Monitor indoor and outdoor air quality with actionable alerts.',
      to: '/industry-solutions/air-quality'
    },
    {
      label: 'Steam Measurement',
      icon: '/images/menu_icons/steam.png',
      description: 'Capture steam flow and thermal performance for process efficiency.',
      to: '/industry-solutions/steam-measurement'
    },
    {
      label: 'Fracto Chatbot',
      icon: '/images/fracto_ani.gif',
      description: 'Deploy an AI chatbot for sales, support, and lead capture in days.',
      href: 'https://chatbot.fractallogy.com/',
      external: true
    },
    {
      label: 'Optical CRM',
      icon: '/images/menu_icons/optical.png',
      description: 'Manage inventory, prescriptions, billing, and customer journeys.',
      to: '/industry-solutions/optical-crm'
    }
  ];

  const featuredVideoEditor = {
    tag: 'Creator Tools',
    title: 'FractoStudio',
    subtitle: 'Online Video Editor',
    icon: '/images/fracto-video-logo.png',
    description:
      'Edit podcasts, Shorts, Reels, and tutorials in your browser. Multi-cam sync, captions, and clean exports — free, private, and watermark-free.',
    chips: ['Free forever', 'No watermark', 'Multi-cam ready'],
    to: '/online-video-editor',
    cta: 'Start editing free'
  };

  const renderSolutionItem = (item) => {
    const itemContent = (
      <>
        <div className="submenu-solution-top">
          <img src={item.icon} alt="" className="submenu-solution-icon" aria-hidden="true" />
          <div className="submenu-solution-heading">
            <span className="submenu-solution-brand">Fracto</span>
            <span className="submenu-solution-label">{item.label}</span>
          </div>
        </div>
        <p className="submenu-solution-desc">{item.description}</p>
        <span className="submenu-solution-cta">TRY NOW</span>
      </>
    );

    if (item.to) {
      return (
        <Link
          key={item.label}
          to={item.to}
          className="submenu-solution-item"
          onClick={() => setProductsSubmenuOpen(false)}
        >
          {itemContent}
        </Link>
      );
    }

    if (item.href) {
      return (
        <a
          key={item.label}
          href={item.href}
          className="submenu-solution-item"
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
          onClick={() => setProductsSubmenuOpen(false)}
        >
          {itemContent}
        </a>
      );
    }

    return (
      <a
        key={item.label}
        href="#"
        className="submenu-solution-item"
        onClick={(e) => e.preventDefault()}
      >
        {itemContent}
      </a>
    );
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (submenuRef.current && !submenuRef.current.contains(event.target)) {
        setProductsSubmenuOpen(false);
      }
    };

    if (productsSubmenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [productsSubmenuOpen]);

  return (
    <>
      {/* Top Header Bar */}
      <div className="top-header">
        <div className="top-header-content">
          <div className="top-header-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <a href="mailto:contact@fractallogy.com">contact@fractallogy.com</a>
          </div>
          <div className="top-header-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span>IT Park, Dehradun, Uttarakhand, India</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="site-header">
        <nav>
          <Link to="/" className="logo">
            <img src="/images/logo.png" alt="Fractallogy Logo" />
            
          </Link>
          
          {/* Desktop Navigation */}
          <ul className="nav-links">
            <li><a href={isHomePage ? "#home" : "/#home"} onClick={() => setProductsSubmenuOpen(false)}>Home</a></li>
            <li><Link to="/about" onClick={() => setProductsSubmenuOpen(false)}>About us</Link></li>
            <li><a href={isHomePage ? "#services" : "/#services"} onClick={() => setProductsSubmenuOpen(false)}>Services</a></li>
            <li 
              className="nav-item-with-submenu"
              ref={submenuRef}
            >
              <a 
                href="#" 
                className="products-link"
                onClick={toggleProductsSubmenu}
              >
                Industry Solutions
                <svg 
                  className={`submenu-arrow ${productsSubmenuOpen ? 'open' : ''}`}
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M3 4.5L6 7.5L9 4.5" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              {productsSubmenuOpen && (
                <div className="submenu submenu-solutions">
                  <div className="submenu-solutions-title">Industrial Automation</div>
                  <div className="submenu-solutions-grid">
                    {solutionGridItems.map((item) => renderSolutionItem(item))}
                    <Link
                      to={featuredVideoEditor.to}
                      className="submenu-solution-featured"
                      onClick={() => setProductsSubmenuOpen(false)}
                    >
                      <div className="submenu-solution-featured-visual">
                        <img
                          src={featuredVideoEditor.icon}
                          alt=""
                          className="submenu-solution-featured-logo"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="submenu-solution-featured-content">
                        <span className="submenu-solution-featured-tag">{featuredVideoEditor.tag}</span>
                        <h4 className="submenu-solution-featured-title">
                          {featuredVideoEditor.title}
                          <span>{featuredVideoEditor.subtitle}</span>
                        </h4>
                        <p className="submenu-solution-featured-desc">{featuredVideoEditor.description}</p>
                        <div className="submenu-solution-featured-chips">
                          {featuredVideoEditor.chips.map((chip) => (
                            <span key={chip}>{chip}</span>
                          ))}
                        </div>
                      </div>
                      <span className="submenu-solution-featured-cta">{featuredVideoEditor.cta}</span>
                    </Link>
                  </div>
                </div>
              )}
            </li>
          </ul>
          <a href={isHomePage ? "#contact" : "/#contact"} className="cta-button desktop-only">Contact Sales</a>
          
          {/* Mobile Menu Button */}
          <button 
            className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`} 
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </nav>
        
        {/* Mobile Navigation Menu */}
        <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-links">
            <li><a href={isHomePage ? "#home" : "/#home"} onClick={closeMobileMenu}>Home</a></li>
            <li><Link to="/about" onClick={closeMobileMenu}>About us</Link></li>
            <li><a href={isHomePage ? "#services" : "/#services"} onClick={closeMobileMenu}>Services</a></li>
            <li className={`mobile-nav-item-with-submenu ${productsSubmenuOpen ? 'submenu-open' : ''}`}>
              <a href="#" onClick={toggleProductsSubmenu}>
                Industry Solutions
                <svg 
                  className={`mobile-submenu-arrow ${productsSubmenuOpen ? 'open' : ''}`}
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M3 4.5L6 7.5L9 4.5" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <ul className={`mobile-submenu ${productsSubmenuOpen ? 'open' : ''}`}>
                {solutionGridItems.map((item) => (
                  <li key={item.label}>
                    {item.to ? (
                      <Link to={item.to} onClick={closeMobileMenu}>
                        <img src={item.icon} alt="" className="mobile-solution-icon" aria-hidden="true" />
                        {item.label}
                      </Link>
                    ) : item.href ? (
                      <a
                        href={item.href}
                        target={item.external ? '_blank' : undefined}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        onClick={closeMobileMenu}
                      >
                        <img src={item.icon} alt="" className="mobile-solution-icon" aria-hidden="true" />
                        {item.label}
                      </a>
                    ) : (
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        <img src={item.icon} alt="" className="mobile-solution-icon" aria-hidden="true" />
                        {item.label}
                      </a>
                    )}
                  </li>
                ))}
                <li className="mobile-submenu-featured">
                  <Link
                    to={featuredVideoEditor.to}
                    onClick={closeMobileMenu}
                    className="mobile-submenu-featured-link"
                  >
                    <img src={featuredVideoEditor.icon} alt="" className="mobile-solution-icon" aria-hidden="true" />
                    <span className="mobile-submenu-featured-text">
                      <strong>{featuredVideoEditor.title}</strong>
                      <span>{featuredVideoEditor.subtitle}</span>
                    </span>
                  </Link>
                </li>
              </ul>
            </li>
            <li><a href={isHomePage ? "#contact" : "/#contact"} className="mobile-cta-button" onClick={closeMobileMenu}>Contact Sales</a></li>
          </ul>
        </div>
      </header>
    </>
  );
};

export default Header;

